import { Integration_Auth_Names_Enum, Integration_Names_Enum } from '@main/graphql/types.generated';
import { z } from 'zod/v4';

import { IntegrationAuth, IntegrationConnector, IntegrationConnectorCategory } from '../../lib';

export type DatadogConnectorConfig = typeof DatadogConnector._.Config;

const DATADOG_SITE_DOMAINS = ['datadoghq.com', 'datadoghq.eu', 'ddog-gov.com'];

/** An optional `api.` or `app.` label, an optional site label such as `us5.`, then a site domain. */
const DATADOG_SITE_HOST = new RegExp(
  String.raw`^(?:api\.|app\.)?(?:[a-z]{2}\d+\.)?(?:${DATADOG_SITE_DOMAINS.join('|').replaceAll('.', '\\.')})$`,
);

/* The messages name their translation keys (params.i18n) for the settings form; the English
 * stays for errors raised outside it. */
// The stored keys are sent to this host, so it must have a Datadog site's shape, matched by
// name: other names under the site domains, such as learn. and partners., are run by third
// parties.
const datadogSiteUrlSchema = z
  .string()
  .refine(
    (value) => {
      if (!URL.canParse(value)) return false;
      const url = new URL(value);
      return (
        url.protocol === 'https:' &&
        DATADOG_SITE_HOST.test(url.hostname) &&
        !DATADOG_SITE_DOMAINS.includes(url.hostname)
      );
    },
    {
      message: 'Use an https:// URL on a Datadog site, such as https://api.datadoghq.com.',
      params: { i18n: 'configureForm.validation.datadogUrl' },
      abort: true,
    },
  )
  .refine(
    (value) => {
      const url = new URL(value);
      // Empty query and fragment delimiters are absent from URL.search and URL.hash.
      return url.port === '' && url.href === `${url.origin}/`;
    },
    {
      message:
        'Datadog site URLs must not include credentials, custom ports, paths, queries, or fragments.',
      params: { i18n: 'configureForm.validation.datadogUrlOnly' },
    },
  )
  // Stored as the bare origin: the Datadog SDK appends paths to it verbatim, so a trailing slash
  // would request `//api/...`. `overwrite` keeps this a string schema for the settings form.
  .overwrite((value) => new URL(value).origin);

export const DatadogConnector = IntegrationConnector.make({
  provider: Integration_Names_Enum.Datadog,
  logoPath: 'images/datadog-logo.svg',
  categories: [IntegrationConnectorCategory.Monitoring],
  auths: IntegrationAuth.group([
    IntegrationAuth.manual({
      name: Integration_Auth_Names_Enum.ApiKey,
      configSchema: z.object({
        baseUrl: datadogSiteUrlSchema,
      }),
      encryptedConfigSchema: z.object({
        apiKey: z.string().min(1),
        appKey: z.string().min(1),
      }),
    }),
  ]),
});
